import { Injectable, EventEmitter } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SignalRService } from './signalr.service';
import { HttpHelper } from './http.service';
import { SettingsService } from './settings.service';
import { AppConfig } from '../model/config';
import { LiveFlight } from '../model/live-flight';
import { LiveFlightEvent } from '../model/liveflight-event';
import * as moment from 'moment';
import { LiveFlightSyncStatus } from '../model/liveflight-sync';
import { DatePipe } from '@angular/common';
import HttpStatusCode from '../shared/HttpStatusCode';
import { CustomError } from '../model/Errors/customErrors';

@Injectable()
export class LiveFlightService {

  private readonly windowStart: string = this.settings.windowStart.toISOString();
  private readonly windowEnd: string = this.settings.windowEnd.toISOString();
  private readonly backendUri: string = this.configuration.backendUrl;

  // Info used to keep track of latest LiveFlight import
  private liveFlightSyncStatus: LiveFlightSyncStatus = null;
  private liveFlightSyncWarningThreshold = 3 * 60 * 1000;
  private liveFlightSyncErrorThreshold = 5 * 60 * 1000;

  private liveFlightEventSubject: BehaviorSubject<LiveFlightEvent[]> = new BehaviorSubject([]);
  public LiveFlightEventsObservable: Observable<LiveFlightEvent[]> = this.liveFlightEventSubject.asObservable();

  public get loaded(): boolean {
    return true;
  }

  public get liveFlightList(): LiveFlightEvent[] {
    return this.liveFlightEventSubject.value;
  }

  public LiveFlightStatusIcon: string = "airplanemode_inactive";
  public LiveFlightStatusColor: string = "green-icon"
  public LiveFlightStatusText: string = "Uppdatering saknas";

  public updateLiveFlights$: EventEmitter<LiveFlightEvent[]>;
  public deleteLiveFlight$: EventEmitter<LiveFlightEvent[]>;
  public liveFlightsLoaded$: EventEmitter<LiveFlightEvent[]>;

  constructor(
    private readonly signalRService: SignalRService,
    private readonly http: HttpHelper,
    private readonly settings: SettingsService,
    private readonly configuration: AppConfig
    ) {

    this.updateLiveFlights$ = new EventEmitter<LiveFlightEvent[]>();
    this.deleteLiveFlight$ = new EventEmitter<LiveFlightEvent[]>();
    this.liveFlightsLoaded$ = new EventEmitter<LiveFlightEvent[]>();


    this.signalRService.updateLiveFlights$.subscribe((liveFlights: LiveFlight[]) => { this.onUpdateLiveFlights(liveFlights); });
    this.signalRService.deleteLiveFlights$.subscribe((liveFlights: LiveFlight[]) => { this.onDeleteLiveFlights(liveFlights); });
    this.signalRService.lastLiveFlightSync$.subscribe((status: LiveFlightSyncStatus) => {
      this.onLastLiveFlightSync(status.syncDate, status.httpStatus, status.message);
    });

  }

  public async initService() {
    // Set a timer to continuously check the LiveFlight import status
    setInterval(() => {
      this.checkLiveFlightSyncStatus();
    }, 15 * 1000);

    await this.loadLiveFlights(this.backendUri, this.windowStart, this.windowEnd);

  }

  public async ackFlight(liveFlightId: string): Promise<any> {

    let body = {
      "liveFlightId": liveFlightId
    };

    return this.http.post(this.backendUri + "/api/liveflightAck/AckFlight", body,
      (result) => { },
      (exception) => { }).toPromise<any>();
  }

  // Buses
  public async setNumberOfBuses(liveFlightId: string, numberOfBuses: string): Promise<any> {

    let body = {
      "liveFlightId": liveFlightId,
      "numberOfBuses": numberOfBuses
    };

    return this.http.post(this.configuration.backendUrl + "/api/liveFlightBus/UpdateNumberOfBuses", body,
      (result) => { },
      (exception) => { }).toPromise<any>();
  }

  private async loadLiveFlights(backendUri: string, windowStart: string, windowEnd: string) {
    console.log("Load liveflights");

    // Liveflights
    await this.http.get(backendUri + "/api/liveFlight/allinwindow?windowStart=" + windowStart + "&windowEnd=" + windowEnd, (data: LiveFlight[]) => {
      this.liveFlightEventSubject.value.splice(0, this.liveFlightEventSubject.value.length);

      data.forEach((flight: LiveFlight) => {
        if (flight.bus) {
          let start: Date = flight.probableTime ? new Date(flight.probableTime.toString()) : new Date(flight.scheduledTime.toString());
          let end = new Date(start.getTime() + (this.configuration.dayPilot.durationOfEventsInMinutes * 60000));

          this.liveFlightEventSubject.value.push(new LiveFlightEvent(flight.id, flight.flightNumber, start, end, flight.id, flight));
        }
      });
      this.liveFlightsLoaded$.emit(this.liveFlightEventSubject.value);
    }, (error) => {
      if (error.status === 401) {
        throw new CustomError(
          {
            name: 'Unauthorized',
            message: 'Unauthorized from server',
            cause: error
          });
      }

    }).toPromise<any>();

  }

  private checkLiveFlightSyncStatus() {
    if (!this.liveFlightSyncStatus) {
      this.LiveFlightStatusIcon = "airplanemode_inactive";
      this.LiveFlightStatusColor = "white-green";
      return;
    }

    // Format the tooltip
    const statusFn = (p1, p2, p3) => `Senaste import: ${p1}, ${p2} - ${p3}`;
    let pipe = new DatePipe(this.settings.getCurrentLocale());
    this.LiveFlightStatusText = statusFn(pipe.transform(this.liveFlightSyncStatus.syncDate, "mediumTime"),
      this.liveFlightSyncStatus.httpStatus,
      this.liveFlightSyncStatus.message);


    switch (this.liveFlightSyncStatus.httpStatus) {
      case HttpStatusCode.OK:
        this.LiveFlightStatusIcon = "airplanemode_active";

        let millisSinceLast = Date.now() - this.liveFlightSyncStatus.syncDate.getTime();
        if (millisSinceLast >= this.liveFlightSyncErrorThreshold) {
          this.LiveFlightStatusIcon = "airplanemode_inactive";
          this.LiveFlightStatusColor = "red-icon";

        } else if (millisSinceLast >= this.liveFlightSyncWarningThreshold) {
          this.LiveFlightStatusColor = "yellow-icon";

        } else {
          this.LiveFlightStatusColor = "green-icon";
        }
        break;

      default:
        this.LiveFlightStatusIcon = "airplanemode_inactive";
        this.LiveFlightStatusColor = "red-icon";
        break;
    }
  }

  private onDeleteLiveFlights(liveFlights: LiveFlight[]) {

    console.log("deleteLiveFlights");
    const liveFlightEvents: LiveFlightEvent[] = [...this.liveFlightEventSubject.getValue()];
    const deletedEvents: LiveFlightEvent[] = [];

    liveFlights.forEach((liveFlight) => {
      const index = liveFlightEvents.findIndex((lf: LiveFlightEvent) => lf.liveFlight.id === liveFlight.id);
      if (index >= 0) {
        // splice returns the removed items, keep them for the delete notification
        deletedEvents.push(...liveFlightEvents.splice(index, 1));
      }
    });

    if (deletedEvents.length > 0) {
      this.liveFlightEventSubject.next(liveFlightEvents);   // remaining flights
      this.deleteLiveFlight$.emit(deletedEvents);           // only the deleted flights
    }
  }

  private onUpdateLiveFlights(updatedLiveFlights: LiveFlight[]) {
    let updates: boolean = false;
    let liveFlightEvents: LiveFlightEvent[] = [...this.liveFlightEventSubject.getValue()];
    updatedLiveFlights.forEach((liveFlight) => {
      updates = true;
      let updatedLf = new LiveFlight(liveFlight);
      const index = liveFlightEvents.findIndex((lf: LiveFlightEvent) => lf.liveFlight.id === updatedLf.id);
      let start: Date = updatedLf.probableTime ? updatedLf.probableTime : updatedLf.scheduledTime;
      let end = new Date(start.getTime() + (this.configuration.dayPilot.durationOfEventsInMinutes * 60000));
      let lfEvent = new LiveFlightEvent(updatedLf.id, updatedLf.flightNumber, start, end, updatedLf.id, updatedLf);
      if (index >= 0) {
        liveFlightEvents[index] = lfEvent
      }
      else if (liveFlight.bus) {
        liveFlightEvents.push(lfEvent);
      }

    });

    if (updates) {
      this.liveFlightEventSubject.next(liveFlightEvents);
      this.updateLiveFlights$.emit(liveFlightEvents);
    }

  }

  private onLastLiveFlightSync(time: Date, httpStatus: number, message: string) {
    this.liveFlightSyncStatus = new LiveFlightSyncStatus(new Date(time), httpStatus, message);
    this.checkLiveFlightSyncStatus();
  }
}
